'use client';

import { Suspense as RouteSuspense } from 'react';
import { PageLoading as RouteLoading } from '@/app/Components/LoadingBubbles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { DiningAuditFixture } from './auditFixture';
import { DiningDirectoryView } from './DiningDirectoryView';
import { diningToday, type DiningLoadError } from './diningViewModel';
import type { FoodItem, MenuResponse } from './types';
import { auditFixtureState } from '@/app/lib/routeStateContract';

function DiningRoute() {
  const state = auditFixtureState(useSearchParams().get('auditFixture'));
  return state ? <DiningAuditFixture state={state} /> : <DiningWorkspace />;
}

export function DiningWorkspace({ embedded = false }: { embedded?: boolean }) {
  const [hall, setHall] = useState('livingston');
  const [meal, setMeal] = useState('dinner');
  const [date, setDate] = useState(diningToday);
  const [menu, setMenu] = useState<MenuResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const menuRequest = useRef<AbortController | null>(null);
  const [error, setError] = useState<DiningLoadError | null>(null);
  const [selectedItem, setSelectedItem] = useState<FoodItem | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [vegetarianOnly, setVegetarianOnly] = useState(false);

  const fetchMenu = useCallback(async () => {
    menuRequest.current?.abort();
    const controller = new AbortController();
    menuRequest.current = controller;
    setLoading(true); setError(null); setMenu(null); setLoadedAt(null); setSelectedItem(null);
    try {
      const params = new URLSearchParams({ hall, meal, date });
      const res = await fetch(`/api/dining/menu?${params}`, { signal: controller.signal });
      if (!res.ok) {
        const message = res.status === 404 ? 'No menu is available for this hall, meal, and date.' : res.status >= 500 ? 'The menu service is unavailable. Try again.' : 'This menu could not load. Try again or choose another hall, meal, or date.';
        throw { message, diagnostic: `Menu service returned HTTP ${res.status}.` } satisfies DiningLoadError;
      }
      const data: MenuResponse = await res.json();
      if (controller.signal.aborted) return;
      setMenu(data);
      setLoadedAt(new Date().toLocaleTimeString([], { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' }));
    } catch (caught: unknown) {
      if (controller.signal.aborted) return;
      setError(typeof caught === 'object' && caught !== null && 'message' in caught && 'diagnostic' in caught ? caught as DiningLoadError : { message: 'Could not reach the menu service. Check your connection and retry.', diagnostic: 'Menu request did not complete.' });
    } finally { if (!controller.signal.aborted) setLoading(false); }
  }, [hall, meal, date]);

  useEffect(() => { void fetchMenu(); return () => menuRequest.current?.abort(); }, [fetchMenu]);
  return <DiningDirectoryView embedded={embedded} model={{ hall, meal, date, menu, loading, error, selectedItem, loadedAt, searchQuery, vegetarianOnly }} actions={{ onHallChange: setHall, onMealChange: setMeal, onDateChange: setDate, onSearchChange: setSearchQuery, onVegetarianChange: setVegetarianOnly, onClearFilters: () => { setSearchQuery(''); setVegetarianOnly(false); }, onRetry: fetchMenu, onSelectItem: setSelectedItem, onCloseItem: () => setSelectedItem(null) }} />;
}

// Query-dependent interactive content owns its loading boundary.
export default function RoutePage() { return <RouteSuspense fallback={<RouteLoading title="Preparing your workspace" />}><DiningRoute /></RouteSuspense>; }
